import type { OrderItem } from '../../../types/apiTypes';
import type { BundleFloorLevel, BundleScanSubsetComponent } from '../../../schemas/apiResponse.schemas';
import type { BundleFloorBinDelta } from '../../../services/bundleFloorAPI';
import type { BundleBinScanMap, BundleWorkSourceScan } from '../../../utils/scanSessionCache';
import { stripByKink } from '@/shared/utils/stripByKink';
import { maxComponentQuantity, pickLineUnitCount, unitsCountFromName } from './orderDataLogic';
import type { ScanMatchResult, ScannedLabel } from './scanningLogic';

export const BUNDLE_WORK_ORDER_SESSION_PREFIX = 'bundle-work:';

/** Bundles still needed on floor: baseline (target) minus current on-hand. */
export function bundlesToMakeOnFloor(floor: Pick<BundleFloorLevel, 'targetQty' | 'quantityOnFloor'>): number {
  return Math.max(0, floor.targetQty - floor.quantityOnFloor);
}

/**
 * Restock trigger for the work order list: each pick bin owns an even share of the baseline
 * (`targetQty / binCount`), and the parent surfaces once any one bin holds less than half of
 * its share. A parent with no known pick bin always surfaces — there is no share to measure
 * it against, and it needs a bin assigned before it can be worked.
 */
export function shouldSurfaceBundleWorkRow(
  floor: Pick<BundleFloorLevel, 'targetQty' | 'bins'>,
): boolean {
  const bins = floor.bins ?? [];
  if (bins.length === 0) {
    return true;
  }
  const halfBinAllocation = floor.targetQty / bins.length / 2;
  return bins.some((bin) => bin.quantity < halfBinAllocation);
}

/** Navbar / summary label, e.g. "1 Bundle" or "3 Bundles". */
export function formatBundlesToMakeLabel(count: number): string {
  return `${count} ${count === 1 ? 'Bundle' : 'Bundles'}`;
}

function pluralBundles(count: number): string {
  return `${count} ${count === 1 ? 'bundle' : 'bundles'}`;
}

export interface BundleWorkPickPrompt {
  readonly title: string;
  readonly subtitle: string;
}

/** Pick-list instruction above the first component chip; the subtitle follows the pick view. */
export function formatBundleWorkPickPrompt(
  count: number,
  view: BundlePickView,
): BundleWorkPickPrompt {
  const shownFor = view === BUNDLE_PICK_VIEW.Total ? count : 1;
  return {
    title: `Make ${pluralBundles(count)}`,
    subtitle: `Showing components for ${pluralBundles(shownFor)}`,
  };
}

export type ParseWholeNumberResult =
  | { readonly ok: true; readonly qty: number }
  | { readonly ok: false; readonly error: string };

/** Change Baseline / Update bin quantity input: a whole number, 0 or more. */
export function parseWholeNumberInput(value: string): ParseWholeNumberResult {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return { ok: false, error: 'Enter a whole number.' };
  }
  const qty = Number.parseInt(trimmed, 10);
  if (!Number.isSafeInteger(qty) || qty < 0) {
    return { ok: false, error: 'Enter a whole number.' };
  }
  return { ok: true, qty };
}

export interface BundleBinSplitInput {
  readonly storageIdPrefix: string;
  readonly value: string;
}

export interface BundleBinSplitEntry {
  readonly storageIdPrefix: string;
  readonly quantity: number;
}

export type ParseBundleBinSplitResult =
  | {
      readonly ok: true;
      readonly entries: readonly BundleBinSplitEntry[];
      readonly total: number;
    }
  | { readonly ok: false; readonly error: string };

/**
 * Parses the per-bin counts a worker entered when completing a work order. Every bin must
 * have a whole number (including 0). The sum is the new on-floor total after replace.
 */
export function parseBundleBinSplit(
  inputs: readonly BundleBinSplitInput[],
): ParseBundleBinSplitResult {
  const entries: BundleBinSplitEntry[] = [];

  for (const input of inputs) {
    const trimmed = input.value.trim();
    if (!trimmed) {
      return { ok: false, error: 'Enter how many are in each bin.' };
    }
    if (!/^\d+$/.test(trimmed)) {
      return { ok: false, error: 'Enter whole numbers only.' };
    }
    const quantity = Number.parseInt(trimmed, 10);
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
      return { ok: false, error: 'Enter whole numbers only.' };
    }
    entries.push({ storageIdPrefix: input.storageIdPrefix, quantity });
  }

  if (entries.length === 0) {
    return { ok: false, error: 'Enter how many are in each bin.' };
  }

  return {
    ok: true,
    entries,
    total: entries.reduce((sum, entry) => sum + entry.quantity, 0),
  };
}

function skuKey(sku: string): string {
  return sku.trim().toUpperCase();
}

/**
 * Appends a unique source label for the return list. Parent pick-bin scans are ignored.
 * Later scans of the same storage-id prefix keep the first location.
 */
export function recordBundleWorkSourceScan(
  current: readonly BundleWorkSourceScan[],
  scannedLabel: ScannedLabel,
  floorParentSku: string,
): readonly BundleWorkSourceScan[] {
  if (skuKey(scannedLabel.sku) === skuKey(floorParentSku)) {
    return current;
  }
  const labelId = scannedLabel.storageIdPrefix.trim().toLowerCase();
  if (!labelId || current.some((scan) => scan.labelId === labelId)) {
    return current;
  }
  return [
    ...current,
    {
      labelId,
      sku: scannedLabel.sku,
      shelfIndex: scannedLabel.shelfIndex,
    },
  ];
}

/** Primary pick-bin shelf index, else the caller's fallback (usually SKU shelf lookup). */
export function resolvePrimaryPickBinShelf(
  bins: readonly { isPrimary: boolean; shelfIndex: string | null }[] | undefined,
  fallback: string,
): string {
  const primary = bins?.find((bin) => bin.isPrimary);
  const primaryShelf = primary?.shelfIndex?.trim();
  if (primaryShelf) {
    return primaryShelf;
  }
  return fallback;
}

/** Primary pick-bin 6-char ID, else the first bin, else N/A. */
export function resolvePrimaryPickBinPrefix(
  bins: readonly { isPrimary: boolean; storageIdPrefix: string }[] | undefined,
): string {
  const primary = bins?.find((bin) => bin.isPrimary);
  const prefix = primary?.storageIdPrefix?.trim() || bins?.[0]?.storageIdPrefix?.trim();
  return prefix || 'N/A';
}

type BundleFloorDecrement = NonNullable<ScanMatchResult['bundleParentFloorDecrement']>;

/**
 * Adds one pending decrement per bundle the scan took off the floor. Keyed by the pick line
 * it satisfied, so a rescan that changes nothing adds nothing and unscanning removes it.
 */
export function recordBundleBinScans(
  current: BundleBinScanMap,
  decrement: BundleFloorDecrement,
): BundleBinScanMap {
  if (decrement.itemIds.length === 0) {
    return current;
  }
  const next: Record<string, { sku: string; storageIdPrefix: string | null }> = { ...current };
  for (const itemId of decrement.itemIds) {
    next[itemId] = {
      sku: decrement.parentSku,
      storageIdPrefix: decrement.storageIdPrefix ?? null,
    };
  }
  return next;
}

/** Drops entries whose pick line is no longer scanned (unscan, Reset, or reloaded order). */
export function pruneBundleBinScans(
  scans: BundleBinScanMap,
  items: readonly OrderItem[],
): BundleBinScanMap {
  const scannedIds = new Set(items.filter((item) => item.scanned).map((item) => item.id));
  const next: Record<string, { sku: string; storageIdPrefix: string | null }> = {};
  for (const [itemId, scan] of Object.entries(scans)) {
    if (scannedIds.has(itemId)) {
      next[itemId] = scan;
    }
  }
  return next;
}

/** Collapses the pending map into one decrement per SKU + bin, ready to POST on submit. */
export function pendingBundleBinDecrements(
  scans: BundleBinScanMap,
  items: readonly OrderItem[],
): BundleFloorBinDelta[] {
  const pruned = pruneBundleBinScans(scans, items);
  const byBin = new Map<string, BundleFloorBinDelta>();

  for (const scan of Object.values(pruned)) {
    const key = `${scan.sku}|${scan.storageIdPrefix ?? ''}`;
    const existing = byBin.get(key);
    byBin.set(
      key,
      existing
        ? { ...existing, quantity: existing.quantity + 1 }
        : {
            sku: scan.sku,
            storageIdPrefix: scan.storageIdPrefix,
            quantity: 1,
            direction: 'decrement',
          },
    );
  }

  return [...byBin.values()];
}

export function bundleWorkOrderSessionId(parentSku: string): string {
  return `${BUNDLE_WORK_ORDER_SESSION_PREFIX}${parentSku.trim()}`;
}

function bundleWorkParentId(parentSku: string): string {
  return `${parentSku.trim()}-bundle-work-parent`;
}

function createStableId(
  sku: string,
  index: string | number,
  parentId: string,
): string {
  return `${parentId}-${sku}-${index}`;
}

export const BUNDLE_PICK_VIEW = {
  Ingredients: 'ingredients',
  Total: 'total',
} as const;

export type BundlePickView = (typeof BUNDLE_PICK_VIEW)[keyof typeof BUNDLE_PICK_VIEW];

function scaleBundleWorkItem(item: OrderItem, multiplier: number): OrderItem {
  const total = pickLineUnitCount(item) * multiplier;
  if (total > 0) {
    return { ...item, isAdjustedQty: true, quantity: total };
  }
  const baseName = (item.name || item.sku).replace(/\s*\(\d+\s*units?\)\s*$/i, '').trim();
  return {
    ...item,
    isAdjustedQty: true,
    quantity: 0,
    name: `${baseName} (0 units)`,
  };
}

/**
 * Suffixes `(1 unit)` so a single-unit chip still shows `- 1 unit`. Consolidation rewrites
 * the suffix whenever a SKU's summed lines exceed one unit.
 */
function withSingleUnitLabel(item: OrderItem): OrderItem {
  const name = item.name || item.sku;
  if (unitsCountFromName(name) !== null) {
    return item;
  }
  return { ...item, name: `${name} (1 unit)` };
}

/**
 * Ingredients keeps the one-bundle pick lines. Total multiplies each line by the
 * number of bundles still needed so the consolidated chip shows the full gather count.
 */
export function bundleWorkItemsForPickView(
  items: readonly OrderItem[],
  view: BundlePickView,
  bundlesToMake: number,
): readonly OrderItem[] {
  if (view === BUNDLE_PICK_VIEW.Ingredients) {
    return items.map(withSingleUnitLabel);
  }
  const multiplier = Number.isFinite(bundlesToMake) && bundlesToMake > 0
    ? Math.floor(bundlesToMake)
    : 0;
  return items.map((item) => withSingleUnitLabel(scaleBundleWorkItem(item, multiplier)));
}

/** Component-only pick lines for one bundle parent (qty 1 bundle). */
export function buildBundleWorkOrderItems(
  parentSku: string,
  components: readonly BundleScanSubsetComponent[],
): OrderItem[] {
  const parentId = bundleWorkParentId(parentSku);
  const items: OrderItem[] = [];

  components.forEach((component, componentIndex) => {
    const componentQuantityPerBundle = component.quantity > 0 ? component.quantity : 1;
    const quantityToCopy =
      componentQuantityPerBundle > maxComponentQuantity ? 1 : componentQuantityPerBundle;
    const isAdjustedQty = componentQuantityPerBundle > maxComponentQuantity;
    const rawComponentName = component.name?.trim() || component.sku;
    const strippedName = stripByKink(rawComponentName) || rawComponentName;
    const componentName = isAdjustedQty
      ? `${strippedName} (${componentQuantityPerBundle} units)`
      : strippedName;

    for (let index = 0; index < quantityToCopy; index += 1) {
      items.push({
        id: createStableId(component.sku, `bw-${componentIndex}-${index}`, parentId),
        scanned: false,
        name: componentName,
        sku: component.sku,
        isComponent: true,
        isAdjustedQty,
        quantity: isAdjustedQty ? componentQuantityPerBundle : undefined,
        parentId,
      });
    }
  });

  return items;
}

/** Parent pick bin is missing when the index is empty, ellipsis, or N/A. */
export function isMissingPickShelf(shelf: string): boolean {
  const normalized = shelf.trim().toUpperCase();
  return normalized === '' || normalized === 'N/A' || normalized === 'NA' || normalized === '…';
}

export function getBundleWorkShelfSkus(parentSku: string, items: readonly OrderItem[]): string[] {
  const skus = new Set<string>([parentSku.trim().toUpperCase()]);
  items.forEach((item) => {
    if (item.sku) {
      skus.add(item.sku.toUpperCase());
    }
  });
  return [...skus];
}
